import { Component, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { VideoModalComponent } from '../../../shared/components/video-modal/video-modal.component';
import { StorageService } from '../../../shared/services/storage.service';
import { Observable, catchError, map, of } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { ApplyButtonComponent } from '../../../shared/components/apply-button/apply-button.component';

interface Testimonial {
  name: string;
  content: string;
  thumbnail: string;
  profileImage: string;
  video: boolean;
  videoUrl?: string;
}

@Component({
  selector: 'app-testimonials-section',
  standalone: true,
  imports: [CommonModule, MatIconModule, RouterModule, MatDialogModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './testimonials-section.component.html',
  styleUrls: ['./testimonials-section.component.scss']
})
export class TestimonialsSectionComponent {
  testimonials: Testimonial[] = [
    {
      name: 'Florian Mayrhuber',
      content: 'Florian\'s Erfolgsgeschichte: Durch HorizonNet konnte er nicht nur erfolgreich Gewicht verlieren, sondern auch seine finanzielle Situation deutlich verbessern. Erfahre in diesem Testimonial-Video, wie er sein Leben nachhaltig verändert hat!',
      thumbnail: 'https://embed-ssl.wistia.com/deliveries/734ac76cd374871d34810b74e82fca9b1b777c9a.webp?image_crop_resized=960x540',
      profileImage: 'https://embed-ssl.wistia.com/deliveries/734ac76cd374871d34810b74e82fca9b1b777c9a.webp?image_crop_resized=960x540',
      video: true,
      videoUrl: 'testimonials/videos/florian-mayrhuber.mov'
    },
    {
      name: 'Simon Enzlmüller',
      content: 'Ich war unzufrieden mit schlecht bezahlten Jobs und suchte nach einem Ausweg. Mit HorizonNet fand ich die richtige Unterstützung und konnte schnell meine ersten vierstelligen Umsätze erzielen. Heute genieße ich die Freiheit der Selbstständigkeit und verdiene endlich das, was ich wert bin!',
      thumbnail: 'https://cdn.prod.website-files.com/66e2a677130668674f6e2b5f/67ebf7251a926f391a151c6f_Testimonial%204.png',
      profileImage: 'https://cdn.prod.website-files.com/66e2a677130668674f6e2b5f/67ebf7251a926f391a151c6f_Testimonial%204.png',
      video: true,
      videoUrl: 'testimonials/videos/simon-enzlmüller.mov'
    }
  ];

  constructor(
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private storageService: StorageService
  ) {}

  ngOnInit(): void {
    // Pre-load video URLs
    this.testimonials.forEach(testimonial => {
      if (testimonial.video && testimonial.videoUrl) {
        this.storageService.getVideoUrl(testimonial.videoUrl)
          .pipe(
            catchError(error => {
              console.error(`Error loading video for ${testimonial.name}:`, error);
              return of(null);
            })
          )
          .subscribe(url => {
            if (url) {
              testimonial.videoUrl = url;
            }
          });
      }
    });
  }

  getTestimonialImage(testimonial: Testimonial): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(testimonial.profileImage);
  }

  formatNameForFilename(name: string): string {
    return name.toLowerCase().replace(/\s+/g, '-');
  }

  openVideo(testimonial: Testimonial, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (!testimonial.video || !testimonial.videoUrl) {
      return;
    }

    // If we already have the URL, open the modal directly
    if (testimonial.videoUrl.startsWith('http')) {
      this.dialog.open(VideoModalComponent, {
        width: '800px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        panelClass: 'video-modal-dialog',
        data: {
          videoUrl: testimonial.videoUrl,
          title: testimonial.name
        }
      });
      return;
    }
    
    // Otherwise, show loading and fetch the URL
    const loadingDialog = this.dialog.open(VideoModalComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'video-modal-dialog',
      data: {
        videoUrl: '',
        title: testimonial.name,
        isLoading: true
      }
    });
    
    this.storageService.getVideoUrl(testimonial.videoUrl)
      .pipe(
        catchError((error: any) => {
          console.error('Error loading testimonial video:', error);
          loadingDialog.close();
          return of(null);
        })
      )
      .subscribe(url => {
        if (url) {
          testimonial.videoUrl = url; // Cache the URL
          loadingDialog.close();
          
          this.dialog.open(VideoModalComponent, {
            width: '800px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            panelClass: 'video-modal-dialog',
            data: {
              videoUrl: url,
              title: testimonial.name
            }
          });
        }
      });
  }
} 